// libs
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

// classes
import TextArea, { IProps as IExtendableAreaProps } from 'src/components/forms/textarea/textarea'

import EmojiPopup from 'src/components/emoji_popup/emoji_popup'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'

import { OglinkFragmentFragment } from '@lopo-tech/lopo-api'
import { useDetectLink } from 'src/utils/comment'
import './text_input.scss'

const THROTTLE_DELAY_MS = 1000
const cls: string = CLASS_PREFIX + 'text-input'

interface IProps extends IExtendableAreaProps {
  invalidState?: boolean;
  onDetectLink?: (link: OglinkFragmentFragment | undefined | null) => void;
  showEmoji?: boolean;
  children?: React.ReactNode
}

export type TextInputHandle = {
  focus: () => void;
  blur: () => void;
};

// eslint-disable-next-line react/display-name
const TextInput = forwardRef((props: IProps, ref: React.ForwardedRef<TextInputHandle>) => {
  const timeoutId = useRef<number>()
  const htmlAreaRef = useRef<HTMLTextAreaElement>()

  const detectLink = useDetectLink()

  useEffect(() => {
    return () => clearTimeout(timeoutId.current)
  }, [])

  const { className, showEmoji, invalidState, children, ...rest } = props

  const startDetectLinkTimeout = (text: string) => {
    const { onDetectLink } = props

    if (!onDetectLink) return

    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }
    timeoutId.current = window.setTimeout(
      () =>
        detectLink(text).then((res) => {
          onDetectLink(res?.data?.fetchOglink?.oglink)
        }),
      THROTTLE_DELAY_MS
    )
  }

  const onInputValueChange = (value: string) => {
    props.onChange?.(value, props.name)
    startDetectLinkTimeout(value)
  }

  useImperativeHandle(ref, () => ({
    focus: () => htmlAreaRef.current?.focus(),
    blur: () => htmlAreaRef.current?.blur()
  }))

  const onEmojiSelected = (native: string) => {
    if (htmlAreaRef.current === null) {
      return
    }
    let value = htmlAreaRef.current?.value ?? ''
    if (htmlAreaRef.current?.selectionStart && htmlAreaRef.current.selectionEnd) {
      value =
        value.substr(0, htmlAreaRef.current.selectionStart) +
        native +
        value.substr(htmlAreaRef.current.selectionEnd)
    } else {
      value += native
    }
    onInputValueChange(value)
  }

  const handleRef = (incomingRef: HTMLTextAreaElement) => {
    htmlAreaRef.current = incomingRef
  }

  return (
    <div className={cls}>
      <TextArea
        textAreaRef={handleRef}
        className={showEmoji ? cls + '--withEmoji ' + className : className}
        {...rest}
        onChange={onInputValueChange}
      />
      <div className={`${cls}__children`}>
        {showEmoji && (
          <div className={`${cls}--emoji`}>
            <EmojiPopup
              onEmojiSelected={onEmojiSelected}
              isInputInvalid={invalidState}
            />
          </div>
        )}
        {children}
      </div>
    </div>
  )
})

export default TextInput
